import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

const DESKTOP = '(min-width: 750px)';

/**
 * Testimonials slider — a translateX slider with drag, autoplay and a numeric counter.
 * Shows 1 slide on mobile and a configurable number of columns on desktop.
 *
 * @typedef {object} LlTestimonialsSliderRefs
 * @property {HTMLElement} [viewport] - Overflow-clipping viewport that captures drag.
 * @property {HTMLElement} [track] - The flex track translated horizontally.
 * @property {HTMLElement[]} [slides] - The individual testimonial slides.
 *
 * @extends {Component<LlTestimonialsSliderRefs>}
 */
export class LlTestimonialsSlider extends Component {
  #index = 0;
  #total = 0;
  #autoplay = false;
  #speed = 7000;
  #colsDesktop = 1;
  #interacting = false;

  /** @type {ReturnType<typeof setInterval> | undefined} */
  #timer;

  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    super.connectedCallback();

    const { track, slides } = this.refs;
    if (!track || !Array.isArray(slides) || slides.length === 0) return;

    if (this.dataset.randomize === 'true' && slides.length > 1) {
      this.#shuffle(track, slides);
    }

    this.#total = this.refs.slides.length;
    this.#speed = Number(this.dataset.speed) || 7000;
    this.#colsDesktop = Math.max(1, Number(this.dataset.colsDesktop) || 1);
    this.#autoplay = this.dataset.autoplay === 'true' && !prefersReducedMotion();

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // Pause autoplay while the user is interacting (pointer or keyboard).
    this.addEventListener('mouseenter', this.#pause, { signal });
    this.addEventListener('mouseleave', this.#resume, { signal });
    this.addEventListener('focusin', this.#pause, { signal });
    this.addEventListener('focusout', this.#resume, { signal });
    window.addEventListener('resize', this.#onResize, { signal });

    this.#initDrag(signal);
    this.#onResize();
    this.#deferQuoteFont();
  }

  /**
   * The quote typeface is applied only when the section is near the viewport, so its woff2
   * is not fetched while the hero (the LCP) is still loading. See the section's data-quote-font.
   */
  #deferQuoteFont() {
    const font = this.dataset.quoteFont;
    if (!font) return;
    const apply = () => this.style.setProperty('--ll-testimonials-font', font);
    if (!('IntersectionObserver' in window)) {
      apply();
      return;
    }
    // Never before the first contentful paint: a web font arriving before the first frame
    // postpones that frame (measured 2026-09-12). Observation starts once the browser has
    // reported FCP (or on load as a fallback).
    const start = () => {
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          apply();
        },
        { rootMargin: '300px 0px' }
      );
      observer.observe(this);
    };
    let started = false;
    const once = () => {
      if (started) return;
      started = true;
      start();
    };
    try {
      new PerformanceObserver((list) => {
        if (list.getEntries().some((e) => e.name === 'first-contentful-paint')) once();
      }).observe({ type: 'paint', buffered: true });
    } catch (e) {
      once();
    }
    window.addEventListener('load', () => setTimeout(once, 0));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearInterval(this.#timer);
    this.#controller?.abort();
  }

  /**
   * Fisher–Yates shuffle of the slide DOM order, then resync refs so the
   * order is different on each visit.
   * @param {HTMLElement} track
   * @param {Element[]} slides
   */
  #shuffle(track, slides) {
    const order = [...slides];
    for (let i = order.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [order[i], order[j]] = [order[j], order[i]];
    }
    for (const slide of order) track.appendChild(slide);
    this.updatedCallback();
  }

  /** Slides visible at once: always 1 on mobile, configurable on desktop. */
  #perView() {
    return window.matchMedia(DESKTOP).matches ? this.#colsDesktop : 1;
  }

  /** Highest valid first-slide index so the last view has no empty gap. */
  #maxIndex() {
    return Math.max(0, this.#total - this.#perView());
  }

  /** Advance to the next view (wraps to the start). Bound via `on:click="/next"`. */
  next() {
    const max = this.#maxIndex();
    this.#go(this.#index >= max ? 0 : this.#index + 1);
  }

  /** Go to the previous view (wraps to the end). Bound via `on:click="/previous"`. */
  previous() {
    const max = this.#maxIndex();
    this.#go(this.#index <= 0 ? max : this.#index - 1);
  }

  /**
   * Arrow-key navigation. Bound from markup via `on:keydown="/handleKeydown"`.
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (event.key === 'ArrowRight') this.next();
    if (event.key === 'ArrowLeft') this.previous();
  }

  /** @param {number} i */
  #go(i) {
    this.#index = Math.min(Math.max(i, 0), this.#maxIndex());
    this.#render();
    this.#restart();
  }

  #render() {
    const { track, slides } = this.refs;
    if (!track || !slides) return;

    // Pixel-accurate offset handles column gaps and multi-column layouts.
    const target = slides[this.#index];
    track.style.transform = `translateX(${target ? -target.offsetLeft : 0}px)`;
  }

  #pause = () => {
    this.#interacting = true;
    clearInterval(this.#timer);
  };

  #resume = () => {
    this.#interacting = false;
    this.#restart();
  };

  /** (Re)start autoplay unless the user is currently interacting or nothing scrolls. */
  #restart() {
    clearInterval(this.#timer);
    if (this.#autoplay && !this.#interacting && this.#maxIndex() > 0) {
      this.#timer = setInterval(() => this.next(), this.#speed);
    }
  }

  /** Recompute layout-dependent state when the breakpoint or width changes. */
  #onResize = () => {
    const max = this.#maxIndex();
    if (this.#index > max) this.#index = max;
    // No scrolling needed when every slide already fits.
    this.classList.toggle('ll-testimonials--static', max === 0);
    this.classList.toggle('ll-testimonials--multi', this.#perView() > 1);
    this.#render();
    this.#restart();
  };

  /** @param {AbortSignal} signal */
  #initDrag(signal) {
    const { viewport, track, slides } = this.refs;
    if (!viewport || !track || !slides) return;

    let startX = 0;
    let dx = 0;
    let dragging = false;
    const base = () => slides[this.#index]?.offsetLeft ?? 0;

    viewport.addEventListener(
      'pointerdown',
      (event) => {
        if (this.#maxIndex() === 0) return;
        dragging = true;
        startX = event.clientX;
        dx = 0;
        this.classList.add('ll-testimonials--dragging');
        viewport.classList.add('is-grabbing');
        clearInterval(this.#timer);
      },
      { signal }
    );

    window.addEventListener(
      'pointermove',
      (event) => {
        if (!dragging) return;
        dx = event.clientX - startX;
        track.style.transform = `translateX(${-base() + dx}px)`;
      },
      { signal }
    );

    const endDrag = (commit) => {
      if (!dragging) return;
      dragging = false;
      this.classList.remove('ll-testimonials--dragging');
      viewport.classList.remove('is-grabbing');

      const threshold = viewport.clientWidth * 0.12;
      if (commit && dx < -threshold) this.next();
      else if (commit && dx > threshold) this.previous();
      else this.#go(this.#index); // snap back
    };

    window.addEventListener('pointerup', () => endDrag(true), { signal });
    window.addEventListener('pointercancel', () => endDrag(false), { signal });
  }
}

if (!customElements.get('ll-testimonials-slider')) {
  customElements.define('ll-testimonials-slider', LlTestimonialsSlider);
}
